import { Box, Button, Group, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconBuilding, IconRefresh } from '@tabler/icons';
import { useNavigate } from 'react-router-dom';
import { SearchInput } from '../../components';
import { useAccounts } from '../../queries/use-accounts';
import { useTableState } from '../../hooks/use-table-state';
import { PAGE_SIZE } from '../../magic-values';
import { AccountsTable } from './common/accounts-table';
import type { AccountDoc } from '../../types/account';

export function AccountsContainer() {
  const navigate = useNavigate();
  const { page, sortBy, sortDir, sortStatus, filterBy, setPage, setSortStatus, setFilterBy } =
    useTableState();
  const { data, isLoading, isFetching, isError, error, refetch } = useAccounts(
    filterBy ? { filterBy } : undefined,
    {
      page,
      pageSize: PAGE_SIZE,
      sortBy,
      sortDir,
    },
  );
  const accounts = data?.items ?? [];

  const handleRowClick = (account: AccountDoc) => {
    navigate(`/account/${account._id}`);
  };

  return (
    <Stack spacing="xl">
      <Box>
        <Group position="apart" align="flex-start" noWrap>
          <Group spacing="md" align="center" noWrap>
            <ThemeIcon size={42} radius="md" variant="light" color="bagel">
              <IconBuilding size={22} />
            </ThemeIcon>
            <Box>
              <Title order={2}>Accounts</Title>
              <Text size="sm" color="dimmed">
                All organizations registered in the system
                {data ? ` · ${data.total} total` : ''}
              </Text>
            </Box>
          </Group>
          <Group spacing="sm" noWrap>
            <SearchInput value={filterBy} onChange={setFilterBy} placeholder="Search accounts…" />
            <Button
              leftIcon={<IconRefresh size={16} />}
              variant="subtle"
              color="bagel"
              size="sm"
              onClick={() => refetch()}
              loading={isFetching}
            >
              Refresh
            </Button>
          </Group>
        </Group>
      </Box>
      {isError ? (
        <Text color="red">
          Failed to load accounts: {(error as Error)?.message ?? 'Unknown error'}
        </Text>
      ) : (
        <Paper withBorder sx={{ overflowX: 'auto' }}>
          <AccountsTable
            accounts={accounts}
            fetching={isLoading}
            onRowClick={handleRowClick}
            page={page}
            totalRecords={data?.total ?? 0}
            onPageChange={setPage}
            sortStatus={sortStatus}
            onSortStatusChange={setSortStatus}
          />
        </Paper>
      )}
    </Stack>
  );
}
